import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Phone,
  Shield,
  Flame,
  HeartPulse,
  ArrowLeft,
  MapPin,
  Share2,
  Camera,
  Copy,
  CheckCircle2,
  Crosshair,
  AlertOctagon,
  Search,
} from "lucide-react";
import { EMERGENCY_COUNTRIES } from "@/data/emergency";
import { useLang } from "@/i18n/LanguageContext";

const EM = {
  page: "emergency-page",
  back: "emergency-back-link",
  search: "country-search-input",
  countryBtn: (code) => `country-btn-${code}`,
  call: (kind) => `call-${kind}-button`,
  detectLoc: "detect-location-button",
  locText: "location-text",
  message: "emergency-message-input",
  photoInput: "emergency-photo-input",
  share: "share-emergency-button",
  copy: "copy-emergency-button",
  shareStatus: "share-status",
};

function CallTile({ label, number, icon: Icon, color, testId }) {
  return (
    <a
      data-testid={testId}
      href={`tel:${number.replace(/[^0-9+]/g, "")}`}
      className="group flex items-center gap-4 p-5 rounded-2xl border border-white/10 bg-card hover:border-red-500/60 hover:bg-red-500/5 transition-all active:scale-[0.98]"
    >
      <div
        className="w-12 h-12 rounded-full grid place-items-center shrink-0"
        style={{ backgroundColor: `${color}1A`, color }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="small-caps text-[10px] text-white/50">{label}</div>
        <div className="font-serif-display text-3xl text-white mt-0.5 leading-tight">
          {number}
        </div>
      </div>
      <Phone className="w-5 h-5 text-white/30 group-hover:text-red-400 transition-colors" />
    </a>
  );
}

export default function EmergencyPage() {
  const navigate = useNavigate();
  const { t: tt, regionName } = useLang();
  const t = tt.emergency;
  const [search, setSearch] = useState("");
  const [code, setCode] = useState("NO");
  const country = useMemo(
    () => EMERGENCY_COUNTRIES.find((c) => c.code === code) || EMERGENCY_COUNTRIES[0],
    [code]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return EMERGENCY_COUNTRIES;
    return EMERGENCY_COUNTRIES.filter((c) =>
      `${regionName(c.code, c.name)} ${c.name}`.toLowerCase().includes(q)
    );
  }, [search, regionName]);
  const countryName = regionName(country.code, country.name);

  /* ------------- Location ------------- */
  const [loc, setLoc] = useState(null); // { lat, lng, accuracy, ts, address? }
  const [locError, setLocError] = useState("");
  const [locLoading, setLocLoading] = useState(false);
  const [address, setAddress] = useState("");

  const detectLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocError(t.noGeo);
      return;
    }
    setLocLoading(true);
    setLocError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const ts = Date.now();
        setLoc({ lat: latitude, lng: longitude, accuracy, ts });
        // Reverse geocode via Nominatim (free, OSM)
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=14&addressdetails=1`,
            { headers: { Accept: "application/json" } }
          );
          const data = await res.json();
          setAddress(data?.display_name || "");
        } catch {
          /* address optional */
        }
        setLocLoading(false);
      },
      (err) => {
        setLocError(t.locFail);
        setLocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  /* ------------- Share ------------- */
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null); // File
  const [shareStatus, setShareStatus] = useState("");

  const photoPreview = useMemo(
    () => (photo ? URL.createObjectURL(photo) : ""),
    [photo]
  );

  useEffect(() => {
    if (!photoPreview) return undefined;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const composedMessage = useMemo(() => {
    const parts = [t.msgHead];
    if (message) parts.push(message);
    if (loc) {
      parts.push(`📍 ${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)} (±${Math.round(loc.accuracy)}m)`);
      parts.push(
        `${t.mapLabel}: https://www.google.com/maps?q=${loc.lat},${loc.lng}`
      );
      if (address) parts.push(`${t.near}: ${address}`);
      parts.push(`${t.timeLabel}: ${new Date(loc.ts).toISOString()}`);
    }
    return parts.join("\n");
  }, [message, loc, address, t]);

  const handleShare = async () => {
    setShareStatus("");
    const shareData = {
      title: t.shareTitle,
      text: composedMessage,
    };
    if (photo && navigator.canShare?.({ files: [photo] })) {
      shareData.files = [photo];
    }
    try {
      if (navigator.share && (!shareData.files || navigator.canShare?.(shareData))) {
        await navigator.share(shareData);
        setShareStatus(t.shared);
      } else {
        // fallback — SMS
        const sms = `sms:?&body=${encodeURIComponent(composedMessage)}`;
        window.location.href = sms;
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        setShareStatus(t.shareFail);
      }
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(composedMessage);
      setShareStatus(t.copied);
      setTimeout(() => setShareStatus(""), 2500);
    } catch {
      setShareStatus(t.copyFail);
    }
  };

  return (
    <div
      data-testid={EM.page}
      className="min-h-screen bg-background text-foreground"
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <button
            data-testid={EM.back}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">{t.back}</span>
          </button>
          <div className="flex items-center gap-2 text-red-500">
            <AlertOctagon className="w-5 h-5" />
            <span className="font-serif-display text-lg md:text-xl tracking-tight">
              {t.title}
            </span>
          </div>
          <div className="w-12" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-16">
        {/* Intro */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold mb-5">
            <AlertOctagon className="w-3.5 h-3.5" />
            {t.alert}
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl tracking-tight">
            {t.h1}
          </h1>
          <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
            {t.intro}
          </p>
        </div>

        {/* Country selector */}
        <section className="mb-10">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <div className="small-caps text-primary text-[11px]">
                {t.step.replace("{n}", "1")}
              </div>
              <h2 className="font-serif-display text-2xl tracking-tight mt-1">
                {t.country}
              </h2>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid={EM.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t.search}
                className="w-full pl-10 pr-4 py-2.5 bg-card border border-white/10 rounded-full text-sm text-white placeholder-white/40 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {filtered.map((c) => {
              const active = c.code === country.code;
              return (
                <button
                  key={c.code}
                  data-testid={EM.countryBtn(c.code)}
                  onClick={() => setCode(c.code)}
                  className={`px-3.5 py-2 rounded-full border text-sm transition-all ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <span className="mr-1.5">{c.flag}</span>
                  {regionName(c.code, c.name)}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <span className="text-white/40 text-sm">
                {t.noMatch} &ldquo;{search}&rdquo;.
              </span>
            )}
          </div>
        </section>

        {/* Call tiles */}
        <section className="mb-12">
          <div className="small-caps text-primary text-[11px]">{t.step.replace("{n}", "2")}</div>
          <h2 className="font-serif-display text-2xl tracking-tight mt-1 mb-6">
            {t.callNow} {country.flag} {countryName}
          </h2>

          {country.general && (
            <a
              data-testid={EM.call("general")}
              href={`tel:${country.general.replace(/[^0-9+]/g, "")}`}
              className="block mb-4 p-6 rounded-2xl border border-red-500/40 bg-gradient-to-br from-red-600/20 to-red-900/10 hover:border-red-500 transition-all"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="small-caps text-red-400 text-[10px]">
                    {t.all}
                  </div>
                  <div className="font-serif-display text-5xl md:text-6xl text-white leading-none mt-1">
                    {country.general}
                  </div>
                </div>
                <div className="w-14 h-14 rounded-full bg-red-500 grid place-items-center text-white shadow-[0_8px_24px_rgba(220,38,38,0.5)]">
                  <Phone className="w-6 h-6" />
                </div>
              </div>
            </a>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CallTile
              label={t.police}
              number={country.police}
              icon={Shield}
              color="#00B2FF"
              testId={EM.call("police")}
            />
            <CallTile
              label={t.fire}
              number={country.fire}
              icon={Flame}
              color="#FF5722"
              testId={EM.call("fire")}
            />
            <CallTile
              label={t.ambulance}
              number={country.ambulance}
              icon={HeartPulse}
              color="#FFB800"
              testId={EM.call("ambulance")}
            />
          </div>

          {t.notes[country.code] && (
            <p data-testid="country-notes" className="mt-4 text-xs text-white/50 max-w-2xl leading-relaxed">
              ⓘ {t.notes[country.code]}
            </p>
          )}
        </section>

        {/* Location share */}
        <section className="mb-16">
          <div className="small-caps text-primary text-[11px]">{t.step.replace("{n}", "3")}</div>
          <h2 className="font-serif-display text-2xl tracking-tight mt-1 mb-6">
            {t.locTitle}
          </h2>

          <div className="rounded-2xl border border-white/10 bg-card p-6 md:p-8">
            <button
              data-testid={EM.detectLoc}
              onClick={detectLocation}
              disabled={locLoading}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60"
            >
              <Crosshair className="w-4 h-4" />
              {locLoading ? t.locating : loc ? t.refresh : t.detect}
            </button>

            {locError && (
              <p className="mt-3 text-sm text-red-400">⚠ {locError}</p>
            )}

            {loc && (
              <div
                data-testid={EM.locText}
                className="mt-5 p-4 rounded-xl bg-black/40 border border-white/5 text-sm font-mono text-white/80 space-y-1"
              >
                <div className="flex items-center gap-2 text-primary">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}
                  </span>
                  <span className="text-white/40">
                    (±{Math.round(loc.accuracy)} m)
                  </span>
                </div>
                {address && (
                  <div className="text-white/60">{t.near}: {address}</div>
                )}
                <a
                  href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1 text-primary hover:underline text-xs"
                >
                  {t.openMaps}
                </a>
              </div>
            )}

            <label className="block mt-6 small-caps text-[10px] text-white/50 mb-2">
              {t.msgLabel}
            </label>
            <textarea
              data-testid={EM.message}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder={t.msgPh}
              className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-primary resize-none"
            />

            <label className="block mt-5 small-caps text-[10px] text-white/50 mb-2">
              {t.photoLabel}
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <label
                htmlFor="emergency-photo"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-white text-sm cursor-pointer hover:bg-white/10 transition-all"
              >
                <Camera className="w-4 h-4" />
                {photo ? t.change : t.choose}
              </label>
              <input
                id="emergency-photo"
                data-testid={EM.photoInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                className="hidden"
              />
              {photo && (
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="text-xs text-white/50 hover:text-white"
                >
                  {t.remove}
                </button>
              )}
              {photoPreview && (
                <img
                  src={photoPreview}
                  alt={t.previewAlt}
                  className="w-20 h-20 object-cover rounded-lg border border-white/10"
                />
              )}
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                data-testid={EM.share}
                onClick={handleShare}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold shadow-[0_8px_24px_rgba(220,38,38,0.45)] active:scale-95 transition-all"
              >
                <Share2 className="w-4 h-4" />
                {t.send}
              </button>
              <button
                data-testid={EM.copy}
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-all"
              >
                <Copy className="w-4 h-4" />
                {t.copy}
              </button>
            </div>

            {shareStatus && (
              <p
                data-testid={EM.shareStatus}
                className="mt-4 text-sm text-primary inline-flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {shareStatus}
              </p>
            )}

            <p className="mt-6 text-[11px] text-white/40 leading-relaxed">
              ⓘ {t.privacy}
            </p>
          </div>
        </section>

        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t.backTrail}
          </Link>
        </div>
      </div>
    </div>
  );
}
